import React, { useEffect, useMemo, useRef, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { usePlayer } from "@/context/PlayerContext";

const STAMP = /^\s*\[(\d{1,2}):(\d{2}(?:\.\d+)?)\]\s?/;

export const parseLyrics = (text) => {
  const lines = (text || "").replace(/\r/g, "").split("\n");
  const out = lines.map((raw) => {
    const m = raw.match(STAMP);
    return m ? { at: parseInt(m[1], 10) * 60 + parseFloat(m[2]), text: raw.replace(STAMP, "") } : { at: null, text: raw };
  });
  return { lines: out, synced: out.some((l) => l.at !== null) };
};

export const LyricsTab = ({ release, className = "mt-6" }) => {
  const text = (release?.lyrics || "").trim();
  const { lines, synced } = useMemo(() => parseLyrics(text), [text]);
  const player = usePlayer();
  const playingThis = player?.track && String(player.track.id).split(":")[0] === release?.id && player.isPlaying;
  const current = playingThis ? player.current : -1;
  const activeIdx = useMemo(() => {
    if (!synced || current < 0) return -1;
    let idx = -1;
    lines.forEach((l, i) => { if (l.at !== null && l.at <= current) idx = i; });
    return idx;
  }, [lines, synced, current]);
  const [open, setOpen] = useState("");
  const bodyRef = useRef();
  useEffect(() => { if (synced && playingThis) setOpen("lyrics"); }, [synced, playingThis]);
  useEffect(() => {
    if (activeIdx < 0 || !bodyRef.current) return;
    const el = bodyRef.current.querySelector(`[data-line="${activeIdx}"]`);
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [activeIdx]);
  if (!text) return null;
  return (
    <Accordion type="single" collapsible className={className} value={open} onValueChange={setOpen} data-testid={`lyrics-${release.id}`}>
      <AccordionItem value="lyrics" style={{ borderColor: "rgba(200,155,60,.2)" }}>
        <AccordionTrigger className="font-caps text-gold hover:no-underline" style={{ fontSize: ".68rem" }} data-testid={`lyrics-toggle-${release.id}`}>
          Lyrics{synced && <span className="text-ash ml-2" style={{ fontSize: ".5rem" }}>· sing along {release.full_song ? "with the song" : "with the preview"}</span>}
        </AccordionTrigger>
        <AccordionContent>
          <div ref={bodyRef} className="max-h-[420px] overflow-y-auto pr-3" data-testid={`lyrics-body-${release.id}`} data-active-line={activeIdx}
               style={{ color: "var(--parchment)", fontSize: ".95rem", lineHeight: 1.75, fontFamily: "var(--display)" }}>
            {lines.map((l, i) => (
              <div key={i} data-line={i} data-testid={i === activeIdx ? `lyrics-active-${release.id}` : undefined}
                   style={{ minHeight: "1.2em", whiteSpace: "pre-wrap", transition: "color .25s, opacity .25s, transform .25s",
                            color: i === activeIdx ? "var(--gold-lift)" : undefined,
                            opacity: synced && activeIdx >= 0 && i !== activeIdx ? .55 : 1,
                            transform: i === activeIdx ? "translateX(6px)" : "none",
                            fontWeight: i === activeIdx ? 700 : 400 }}>
                {l.text}
              </div>
            ))}
          </div>
          <p className="font-caps text-ash mt-3" style={{ fontSize: ".5rem" }}>Lyrics © {release.artist || "P. Frazy"} · Heart N Soul Records</p>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
